import React, { useState, useMemo } from 'react'
import {
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarProps,
  GridValidRowModel,
  ToolbarPropsOverrides,
} from '@mui/x-data-grid'
import { DataGrid, DataGridProps } from '@mui/x-data-grid'
import { Evidence } from '@trustero/trustero-api-web/lib/receptor_v1/receptor_pb'
import { Struct } from '@trustero/trustero-api-web/lib/receptor_v1/receptor_pb'
import { getNoEvidenceMessage } from 'src/components/ModalForms/Evidence/ViewEvidenceForm/ViewEvidenceForm.utils'
import { getEvidenceRows } from 'src/Utils/Evidence/evidence.helpers'
import { getEvidenceColumns } from 'src/Utils/Evidence/evidence.helpers'
import { Markup } from 'src/components/Reusable/Text/Markup'
import {
  SortAscSvg,
  SortDescSvg,
  SortNoneSvg,
} from 'src/components/Icons/Basic'
import isFunction from 'lodash/isFunction'
import { CloseButton } from 'src/components/Reusable/Document/DocumentViewer.components'
import { useToggleDocumentViewer } from 'src/components/Reusable/Document/DocumentViewer.hooks'
import { useSearchableEvidenceInstance } from 'src/pages/Evidence/evidence.hooks'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import { Search } from 'src/components/Reusable/Search/Search'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { PlainTooltip } from 'src/components/Reusable/Tooltip/PlainTooltip/PlainTooltip'
import { TooltipPositions } from 'src/components/Reusable/Tooltip/Tooltip'
import { useHasRequiredPermissions } from 'src/app/AppAuth/AppAuth.hooks'
import { PERMISSIONS } from 'src/config/roleConfig'
import { SimpleIconButton } from 'src/components/Reusable/Buttons/SimpleIconButton'
import {
  AutomaticEvidenceContainer,
  EvidenceDataGridContainer,
  EvidenceShowTabBody,
  StyledCsvBody,
  StyledCsvModal,
} from '../../../../evidence.styles'
import {
  AutomaticEvidenceToolbarContainer,
  ColumnsMenuContainer,
} from './AutomaticEvidenceBody.styles'
import { ExpandEvidenceIcon } from './AutomaticEvidenceBody.styles'

export const ExportToolbar = (): JSX.Element => (
  <GridToolbarContainer>
    <GridToolbarExport />
  </GridToolbarContainer>
)

export const EvidenceDataGrid = (
  props: DataGridProps & {
    toolbar?: React.JSXElementConstructor<
      GridToolbarProps & ToolbarPropsOverrides
    >
  },
): JSX.Element => (
  <DataGrid
    {...props}
    sx={{
      fontFamily: 'Poppins',
    }}
    getRowClassName={(params) => {
      let baseClass = params.row.className || '' // Preserve existing classes
      baseClass = `${
        isFunction(props.getRowClassName) && props.getRowClassName(params)
      } ${baseClass}` // Add custom classes
      return params.indexRelativeToCurrentPage % 2 === 0
        ? `${baseClass} even`
        : `${baseClass} odd`
    }}
    initialState={{
      ...props.initialState,
      sorting: {
        ...props.initialState?.sorting,
        sortModel: props.initialState?.sorting?.sortModel ?? [
          {
            field: props.columns[0]?.field,
            sort: 'asc',
          },
        ],
      },
    }}
    slots={{
      columnSortedAscendingIcon: SortAscSvg,
      columnSortedDescendingIcon: SortDescSvg,
      columnUnsortedIcon: SortNoneSvg,
      toolbar: props.toolbar,
    }}
    slotProps={{
      pagination: {
        showFirstButton: true,
        showLastButton: true,
      },
    }}
  />
)

export const AutomaticEvidenceTable = ({
  rows,
  columns,
}: {
  rows: GridValidRowModel[]
  columns: GridColDef[]
}): JSX.Element => {
  return (
    <EvidenceDataGridContainer>
      <EvidenceDataGrid rows={rows} columns={columns} />
    </EvidenceDataGridContainer>
  )
}

export const ColumnsMenu = ({
  columns,
  selectedColumns,
  onChange,
}: {
  columns: GridColDef[]
  selectedColumns: GridColDef[]
  onChange: (field: string) => void
}): JSX.Element => {
  const getIsChecked = (column: GridColDef) => {
    return selectedColumns.some((c) => c.field === column.field)
  }

  return (
    <ColumnsMenuContainer>
      {columns.map((column) => (
        <CheckBoxInput
          key={column.field}
          label={column.headerName}
          checked={getIsChecked(column)}
          onChange={() => onChange(column.field)}
        />
      ))}
    </ColumnsMenuContainer>
  )
}

export const AutomaticEvidenceToolbar = ({
  toggleModal,
}: {
  toggleModal: () => void
}): JSX.Element => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const hasPermissions = useHasRequiredPermissions([PERMISSIONS.READ])

  return (
    <AutomaticEvidenceToolbarContainer>
      <PlainTooltip
        id="expand-evidence-tooltip"
        tooltipBody="Expand table"
        placement={TooltipPositions.top}
        disabled={!hasPermissions}
      >
        <SimpleIconButton onClick={toggleModal}>
          <ExpandEvidenceIcon />
        </SimpleIconButton>
      </PlainTooltip>
      <Search
        isExpanded={isSearchExpanded}
        setIsExpanded={setIsSearchExpanded}
        paramPrefix="evidence"
      />
    </AutomaticEvidenceToolbarContainer>
  )
}

export const AutomaticEvidenceBody = ({
  body,
}: {
  body?: Uint8Array
}): JSX.Element => {
  const { isModalOpen, toggleModal } = useToggleDocumentViewer()

  const evidence: Evidence = Evidence.deserializeBinary(
    body || new Uint8Array(),
  )
  const evidenceStruct: Struct = useMemo(
    () => evidence.getStruct() || new Struct(),
    [evidence],
  )
  const noEvidence = evidenceStruct.getRowsList().length === 0
  const noEvidenceMessage = getNoEvidenceMessage(evidence)

  const evidenceRows = useMemo(
    () => getEvidenceRows(evidenceStruct),
    [evidenceStruct],
  )
  const evidenceColumns = useMemo(
    () => getEvidenceColumns(evidenceStruct),
    [evidenceStruct],
  )

  const searchableEvidenceRows = useSearchableEvidenceInstance([
    ...evidenceRows,
  ])
  const { data: matchingRows } = useApplySearch(
    searchableEvidenceRows,
    'evidence',
  )

  if (!body) return <></>

  return (
    <AutomaticEvidenceContainer>
      <AutomaticEvidenceToolbar toggleModal={toggleModal} />
      {noEvidence ? (
        <EvidenceShowTabBody>
          <Markup markdown={noEvidenceMessage} />
        </EvidenceShowTabBody>
      ) : (
        <>
          <AutomaticEvidenceTable
            rows={matchingRows}
            columns={evidenceColumns}
          />
        </>
      )}
      {isModalOpen && (
        <StyledCsvModal>
          <StyledCsvBody>
            <AutomaticEvidenceTable
              rows={matchingRows}
              columns={evidenceColumns}
            />
          </StyledCsvBody>
          <CloseButton onClick={toggleModal} />
        </StyledCsvModal>
      )}
    </AutomaticEvidenceContainer>
  )
}
